/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ArActionButton } from './ar-action-button';
import { ArErrorBanner } from './ar-error-banner';
import { ARHelper } from '../../utils/ar-helper';
import { ArLoadingBanner } from './ar-loading-banner';
import { ARStatus } from '../../utils/ar-status.enum';
import { CanvasShell } from './canvas-shell';
import { getSceneObjectClickHandler, registerSceneObjectType } from '../../interaction';
import { injectLoadingStyles } from '../inject-loading-styles';
import { RENDERING_CAPTURE_HEIGHT, RENDERING_CAPTURE_WIDTH } from '../../../capture/rendering-capture.constants';
import { RenderingOverlay } from './rendering-overlay';
import { VALIDATION_MARKER_TYPE } from '../../../controller/functions/validation-markers';

import { ThreeSceneEngine } from '../../engine';
import { KitchenSceneBuilder } from '../../engine/builders';
import type { KitchenBlueprintInput } from '../../engine/builders';
import type { ImageRenderParams, RenderKitchenProps } from '../../types/react-props';
import { createThreeInstanceAdapter, validateAndFixMaterialKeys } from './helpers';

// A validation badge is not a selectable object — clicking one opens its notification in the app.
registerSceneObjectType({
	type: VALIDATION_MARKER_TYPE,
	onClick: ({ action, objectId, userData, publish }) => {
		if (action !== 'click') return;
		publish('blueprint_validation', 'show', { ref: objectId, old: null, new: userData.notificationIds ?? null });
	}
});

/** Walk up from a hit object to the enclosing scene object of `type` (the island group, say). */
const findAncestorOfType = (object: any, type: string): any => {
	let current = object;
	while (current) {
		if (current.userData?.type === type) return current;
		current = current.parent;
	}
	return null;
};

/**
 * Recalculate and apply kitchen debug data (scene center, debug circles/spheres).
 * Called during initial build and after rebuilds triggered by shape changes.
 */
const updateKitchenDebugData = (kitchen: unknown, engine: ThreeSceneEngine): void => {
	if (!(kitchen as any)?.getSize) return;

	try {
		validateAndFixMaterialKeys(kitchen);

		const kitchenSize = (kitchen as any).getSize(true);
		const kitchenDebugData = {
			origin: kitchenSize.origin || { x: 0, y: 0, z: 0 },
			size: {
				width: kitchenSize.width || 0,
				height: kitchenSize.height || 0,
				depth: kitchenSize.depth || 0
			},
			left: kitchenSize.left ? {
				origin: kitchenSize.left.origin || { x: 0, y: 0, z: 0 },
				size: {
					width: kitchenSize.left.size?.width || 0,
					height: kitchenSize.left.size?.height || 0,
					depth: kitchenSize.left.size?.depth || 0
				}
			} : undefined,
			right: kitchenSize.right ? {
				origin: kitchenSize.right.origin || { x: 0, y: 0, z: 0 },
				size: {
					width: kitchenSize.right.size?.width || 0,
					height: kitchenSize.right.size?.height || 0,
					depth: kitchenSize.right.size?.depth || 0
				}
			} : undefined,
			defaultIslandX: 0
		};

		engine.setSceneCenterFromKitchenData(kitchenDebugData);

		if (engine.debugMode) {
			engine.setKitchenDebugData(kitchenDebugData);
		}
	} catch (error) {
		console.warn('Render3DKitchen: Failed to get kitchen size for scene center:', error);
	}
};

/**
 * Render3DKitchen - Uses the new ThreeSceneEngine architecture.
 *
 * This component renders a 3D kitchen scene using the modular engine
 * with separate managers for scene, camera, textures, models, etc.
 */
export const Render3DKitchen = (props: RenderKitchenProps): React.ReactElement => {
	const ref = useRef<HTMLDivElement>(null);
	const engineRef = useRef<ThreeSceneEngine | null>(null);
	// Click bridges registered per build; kept so a rebuild replaces them instead of stacking duplicates
	const clickBridgesRef = useRef<Array<{ type: string; handler: (event: any) => void }>>([]);
	const builderRef = useRef<KitchenSceneBuilder | null>(null);
	const isInitialRender = useRef<boolean>(true);
	const isInitialRender2 = useRef<boolean>(true);
	const isInitialKitchenBuild = useRef<boolean>(true);
	const [isRendering, setIsRendering] = useState<boolean>(false);
	const [isARAvailable, setIsARAvailable] = useState<boolean>(false);
	const arEnabled = props.arEnabled ?? true;
	const [arError, setArError] = useState<string | null>(null);
	const [isARLoading, setIsARLoading] = useState<boolean>(false);
	const [arStatus, setArStatus] = useState<ARStatus>(ARStatus.NOT_PRESENTING);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);
	const [isBuildComplete, setIsBuildComplete] = useState<boolean>(false);
	const [initError, setInitError] = useState<string | null>(null);

	// Inject loading animation styles on mount
	useEffect(() => {
		injectLoadingStyles();
	}, []);

	// Initialize engine on mount
	useEffect(() => {
		if (!ref.current) return;

		// Probe WebGL before touching the engine — a blocked GPU (hardware acceleration off) makes
		// context creation throw on every retry, leaving a silent blank canvas. Surface it instead.
		const glProbe = document.createElement('canvas');
		const glOk = glProbe.getContext('webgl2') || glProbe.getContext('webgl');
		if (!glOk) {
			setInitError('3D preview is unavailable — hardware acceleration may be disabled in your browser. Enable it in your browser settings, then reload.');
			return;
		}
		setInitError(null);

		const container = ref.current;
		const quality = props.controller?.options?.renderQuality ?? 7;
		const textureQuality = props.controller?.options?.textureQuality ?? null;
		const configuredBaseUrl = props.controller?.options?.assetsBaseUrl;
		const basePath = configuredBaseUrl
			? (configuredBaseUrl.endsWith('/') ? configuredBaseUrl : `${configuredBaseUrl}/`)
			: 'https://web-assets.rtaoutdoorliving.com/assets/';

		// Create engine with progressive rendering options
		const progressiveRender = props.controller?.progressiveRender ?? false;
		const engineConfig = props.controller?.engineConfig;
		const engine = new ThreeSceneEngine({
			container,
			backgroundColor: props.backgroundColor || '#ffffff',
			quality: quality as any,
			textureQuality,
			xrEnabled: true,
			basePath,
			progressiveRender,
			progressiveFadeInDuration: 300,
			dissolveOutDuration: 400,
			pixelRatio: engineConfig?.pixelRatio,
			antialias: engineConfig?.antialias,
			resizeDebounceHz: engineConfig?.resizeDebounceHz,
			autoZoomPadding: props.controller?.options?.autoZoomPadding,
			autoZoomCameraHeightBoost: props.controller?.options?.autoZoomCameraHeightBoost,
			autoZoomStyle: props.controller?.options?.autoZoomStyle
		});

		// Abort flag — set by cleanup to prevent stale async continuations
		// (React StrictMode re-mounts, disposing the engine while loads are in-flight).
		let aborted = false;

		// MUST catch: an unhandled initialize() rejection (e.g. WebGL context exhaustion) leaves a permanently dead canvas — no build, no error, every readiness wait times out
		const initWithRetry = async (attempt = 1): Promise<void> => {
			try {
				await engine.initialize();
			} catch (initErr) {
				console.error(`[RENDER-3D] engine.initialize failed (attempt ${attempt}):`, initErr);
				if (aborted || attempt >= 2) throw initErr;
				await new Promise((resolve) => setTimeout(resolve, 1000));
				return initWithRetry(attempt + 1);
			}
		};
		(async () => {
			await initWithRetry();
			if (aborted) return;
			engineRef.current = engine;

			// Dev-only handle so the dev-bridge can inspect the live engine and its managers
			if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) (window as unknown as { __rtaEngine?: ThreeSceneEngine }).__rtaEngine = engine;

			// Create and register kitchen builder (managers are now available)
			const kitchenBuilder = new KitchenSceneBuilder({
				basePath,
				renderQuality: Math.abs(quality),
				textureQuality,
				qualitySettings: null,
				textureManager: engine.textureManager,
				modelManager: engine.modelManager,
				cutoutManager: engine.cutoutManager,
				onSkyReady: (skyKey: string) => {
					engine.changeSkyTexture(skyKey);
				},
				getBuildGeneration: () => engine.buildGenerationAccess
			});
			builderRef.current = kitchenBuilder;
			engine.registerBuilder('kitchen', kitchenBuilder);

			// Subscribe to render events — only show spinner for full rebuilds,
			// not incremental transitions (e.g. appliance removal with autoSize)
			engine.on('render:start', (event) => {
				const isRebuild = (event.data as { isRebuild?: boolean } | undefined)?.isRebuild !== false;
				if (isRebuild) {
					setIsRendering(true);
				}
				props.controller?.setRendering?.(true);
			});
			engine.on('render:complete', () => {
				setIsRendering(false);
				props.controller?.setRendering?.(false);
			});

			// Subscribe to progressive rendering events
			if (progressiveRender) {
				engine.on('object:added', (event) => {
					const data = event.data as { id: string; type: string } | undefined;
					if (data) {
						props.controller?.onObjectAdded?.(data.id, data.type);
					}
				});
				engine.on('object:removing', (event) => {
					const data = event.data as { id: string } | undefined;
					if (data) {
						props.controller?.onObjectRemoving?.(data.id);
					}
				});
			}

			// Double-click on an appliance toggles the rotation gizmo
			engine.on('object:dbl-clicked', (event) => {
				const result = event.data as { objectId?: string; object?: { userData?: Record<string, unknown> } } | undefined;
				if (!result?.objectId) {
					return;
				}
				const { objectId, object } = result;
				const userData = object?.userData ?? {};
				if (userData.type !== 'appliance') {
					return;
				}

				// Only allow rotation gizmo on top-only appliances
				const placement = (userData.subtype || userData.placement || '') as string;
				if (!placement.toLowerCase().includes('top')) {
					return;
				}

				const gizmoManager = engine.rotationGizmoManager;
				if (!gizmoManager) return;

				if (gizmoManager.isActive && gizmoManager.activeObjectId === objectId) {
					// Same appliance double-clicked again -- toggle off
					engine.deactivateRotationGizmo();
				} else {
					// Different appliance or no gizmo active -- activate on this one
					engine.activateRotationGizmo(objectId);
				}
			});

			// Listen for rotation:complete events and commit to the domain model
			engine.on('rotation:complete', (event) => {
				const { objectId, axis, angle } = event.data as { objectId: string; axis: 'x' | 'y' | 'z'; angle: number };
				console.log('[Render3DKitchen] rotation:complete received', { objectId, axis, angle, hasRotateAppliance: !!props.controller?.rotateAppliance });
				if (props.controller?.rotateAppliance) {
					props.controller.rotateAppliance({ objectId, axis, angle });
				}
			});

			// Connect engine to controller early so rotation gizmo commits
			// trigger scene rebuilds even during the initial progressive build.
			// The adapter is updated again after the first build completes.
			const earlyAdapter = createThreeInstanceAdapter(engine);
			props.instanceRef?.(earlyAdapter);
			if (props.ownsController !== false && props.controller?.setThreeInstance) {
				props.controller.setThreeInstance(earlyAdapter);
			}

			// Load scene foundation (sky + floor) before anything else renders.
			// This blocks until default-res textures are loaded so the foundation
			// is visible immediately. Quality upgrades happen async in parallel.
			const skyKey = props.skyKey || 'sky/overcast';
			const floorKey = props.floorKey;
			const defaultCameraDistance = (props.controller?.options as any)?.defaultCameraDistance ?? 30;
			await engine.loadSceneFoundation({
				skyKey,
				floorKey,
				quality: Math.abs(quality),
				defaultCameraDistance
			});

			if (aborted) return;
			setIsInitialized(true);
		})().catch((err) => {
			console.error('[RENDER-3D] initialization pipeline failed permanently:', err);
			if (!aborted) setInitError('3D preview could not start. Please enable hardware acceleration in your browser settings, then reload.');
		});

		return () => {
			aborted = true;
			props.instanceRef?.(null);
			// Clear the controller's instance ref FIRST — waits/exports must not target a disposed engine.
			// Identity-scoped: this engine must not blank a ref another mounted viewport owns.
			const controllerRef = props.controller as { clearThreeInstance?: (i: unknown) => void; setThreeInstance?: (i: unknown) => void } | undefined;
			if (props.ownsController === false) {
				// never claimed the slot — nothing to release
			} else if (controllerRef?.clearThreeInstance) {
				controllerRef.clearThreeInstance(createThreeInstanceAdapter(engine));
			} else if (controllerRef?.setThreeInstance) {
				controllerRef.setThreeInstance(null as never);
			}
			engine.dispose();
			engineRef.current = null;
			builderRef.current = null;
			setIsInitialized(false);
		};
	}, []);

	// Build kitchen when initialized and kitchen data is available.
	// When kitchen is null (no blueprint), the empty scene (sky + floor) from
	// loadSceneFoundation is displayed with the default camera.
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized || !props.kitchen) return;

		const isFirstBuild = isInitialKitchenBuild.current;

		const buildInput: KitchenBlueprintInput = {
			kitchen: props.kitchen,
			controller: props.controller,
			floorKey: props.floorKey,
			skyKey: props.skyKey,
			backgroundColor: props.backgroundColor
		};

		// Reset build state
		setIsBuildComplete(false);

		// Set up scene center and debug visualization BEFORE building
		// This ensures _sceneCenter is available for flyFromPresentationPoint animations during progressive rendering
		updateKitchenDebugData(props.kitchen, engine);

		// For first build: use composeProgressiveWithTypes for type-based animations, otherwise compose
		// For subsequent builds (blueprint replacement): use dissolveAndRebuild with configured transition mode
		const useProgressiveRender = props.controller?.progressiveRender ?? false;
		const blueprintTransition = (props.controller?.options as any)?.blueprintTransition ?? 'dissolve';

		let buildPromise: Promise<void>;
		console.warn('[RENDER-3D] Building scene, progressive:', useProgressiveRender);
		if (isFirstBuild) {
			buildPromise = useProgressiveRender
				? engine.composeProgressiveWithTypes([{ builder: 'kitchen', data: buildInput }])
				: engine.compose([{ builder: 'kitchen', data: buildInput }]);
		} else {
			// Blueprint replacement: dissolve/crossfade/instant based on configured mode
			buildPromise = engine.dissolveAndRebuild(
				[{ builder: 'kitchen', data: buildInput }],
				useProgressiveRender,
				blueprintTransition
			);
		}

		buildPromise
			.then(() => {
				setIsBuildComplete(true);
				isInitialKitchenBuild.current = false;

				// Show rotation axis arcs when the controller requests them (data-manager preview)
				if (props.controller?.options?.showRotationArcs) {
					engine.createRotationAxisArcs();
				}

				// Always connect engine to controller for tool interface
				const builtAdapter = createThreeInstanceAdapter(engine);
				props.instanceRef?.(builtAdapter);
				if (props.ownsController !== false && props.controller?.setThreeInstance) {
					props.controller.setThreeInstance(builtAdapter);
				}

				// Bridge engine click events into the controller's pub/sub system
				const publish = (props.controller as any)?.publish as ((noun: string, action: string, payload: any, payloadType?: string) => void) | undefined;
				if (publish) {
					const buildObjectPayload = (event: any): { noun: string; payload: { id: string; type: string; name: string; data: Record<string, any> | null } } => {
						const { objectId, object, point } = event.data;
						const ud = object?.userData ?? {};
						const isSelection = ud.type === 'selection-label';
						const noun = isSelection ? 'selections' : 'object';
						const objectType: string = ud.type ?? 'unknown';
						const name: string = ud.sku ?? ud.label ?? objectType;
						// The countertop mesh keeps a synthetic id for scene diffing, so publish the domain one
						const id: string = objectType === 'countertop' && ud.countertopId ? String(ud.countertopId) : objectId;
						// Where on the object the ray landed, in the island's own frame — the app turns this
						// into the panel-relative position its context menu actions expect.
						const islandGroup = findAncestorOfType(object, 'island');
						const local = point && islandGroup ? islandGroup.worldToLocal(point.clone()) : null;
						const data = {
							islandId: islandGroup?.userData?.id ?? ud.islandId ?? null,
							panelItemGroupId: ud.panelItemGroupId ?? null,
							localPoint: local ? { x: local.x, y: local.y, z: local.z } : null,
							// An overlay carries whatever its author needs the app to receive
							...(ud.eventData as Record<string, unknown> ?? {})
						};
						return { noun, payload: { id, type: objectType, name, data } };
					};

					// A blueprint swap re-runs this block; drop the previous bridges first or every
					// click publishes once per build that ever ran on this engine.
					for (const { type, handler } of clickBridgesRef.current) engine.off(type, handler);
					clickBridgesRef.current = [];
					const bridge = (type: string, action: string): void => {
						const handler = (event: any): void => {
							const userData = event?.data?.object?.userData ?? {};
							const registered = getSceneObjectClickHandler(userData.type);
							if (registered) {
								registered({ action, objectId: event.data.objectId, userData, publish: publish.bind(props.controller) });
								return;
							}
							const { noun, payload } = buildObjectPayload(event);
							publish.call(props.controller, noun, action, payload, 'object');
						};
						engine.on(type, handler);
						clickBridgesRef.current.push({ type, handler });
					};
					// Hover is its own shape: it publishes null when the pointer leaves everything, which the
					// click payload builder has no way to express.
					const hoverHandler = (event: any): void => {
						const result = event?.data ?? null;
						if (!result) {
							publish.call(props.controller, 'object', 'hover', { id: null, type: null, name: null, data: null }, 'object');
							return;
						}
						const { payload } = buildObjectPayload(event);
						publish.call(props.controller, 'object', 'hover', payload, 'object');
					};
					engine.on('object:hovered', hoverHandler);
					clickBridgesRef.current.push({ type: 'object:hovered', handler: hoverHandler });

					bridge('object:clicked', 'click');
					bridge('object:dbl-clicked', 'double-click');
					bridge('object:right-clicked', 'right-click');
				}

				// Thumbnail render costs 0.2-0.5s of main-thread jank — defer it off the ready path
				const renderThumbnail = (): void => {
					if (engine.isDisposedAccess) return;
					const camera = engine.sceneManager?.camera;
					const originalFov = camera?.fov;
					if (camera) {
						camera.fov = props.imageFov ?? 30;
						camera.updateProjectionMatrix();
					}
					const imageData = engine.getImageRender({ pixelRatio: 2, width: RENDERING_CAPTURE_WIDTH, height: RENDERING_CAPTURE_HEIGHT });
					if (camera && originalFov) {
						camera.fov = originalFov;
						camera.updateProjectionMatrix();
					}
					// A non-owning preview must not overwrite the controller's thumbnail — the favorite
					// and save flows poll that image, and it belongs to the primary viewport.
					if (imageData && props.ownsController !== false) {
						props.controller?.setImage?.(imageData);
					}
				};
				if (typeof window.requestIdleCallback === 'function') {
					window.requestIdleCallback(() => renderThumbnail(), { timeout: 2000 });
				} else {
					window.setTimeout(renderThumbnail, 300);
				}
			})
			.catch((error) => {
				console.error('Render3DKitchen: Error building kitchen:', error);
			});
	}, [isInitialized, props.kitchen]);

	// Subscribe to @Publish events from the kitchen domain model and controller.
	// When the domain model changes (appliance moved, panel resized, material changed,
	// etc.) the @Publish decorator fires events. We subscribe to all of them and call
	// engine.requestRender() so that the 3D view reflects the latest state even when
	// the continuous render loop is not running.
	useEffect(() => {
		const engine = engineRef.current;
		const { controller } = props;
		const kitchen = controller?.kitchen;
		if (!engine || !isInitialized || !controller || !kitchen) return;

		const subscriptionIds: Array<string> = [];

		// Subscribe to all kitchen domain events (appliance, island, panel, material, etc.)
		subscriptionIds.push(
			kitchen.subscribe('*', '*', () => { engine.requestRender(); })
		);

		// Subscribe to all controller events (navigation, view mode, zoom, etc.)
		subscriptionIds.push(
			controller.subscribe('*', '*', () => { engine.requestRender(); })
		);

		return () => {
			for (const subId of subscriptionIds) {
				controller.unsubscribe(subId);
			}
		};
	}, [isInitialized, props.controller, props.controller?.kitchen]);

	// After a resize burst settles, invoke the reset_view tool to re-fit the
	// camera to the new aspect ratio. The scene-manager emits 'resize:settled'
	// once the container size stops changing and an actual size change was
	// applied, so this won't fire mid-resize.
	//
	// We defer the reset_view call through two rAFs so it runs after the browser
	// has fully laid out the new container size and camera-controls has applied
	// the resize's aspect change. Invoking reset_view before that leaves the
	// camera at the wrong auto-zoom position.
	//
	// Controller is read from a ref at fire time so controller-prop re-renders
	// don't tear down and re-install the listener (which could drop an in-flight
	// 'resize:settled' emit).
	const controllerRef = useRef(props.controller);
	controllerRef.current = props.controller;

	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized || !isBuildComplete) return;

		let rafId: number | null = null;

		const handleSettled = (): void => {
			// A resize mid-drag (mobile URL bar) must not re-frame the camera under the finger:
			// wait for the drag to end, then re-frame.
			if (engine.isApplianceDragActive) {
				const onDragState = (event: { data?: unknown }): void => {
					const phase = (event.data as { phase?: string } | undefined)?.phase;
					if (phase === 'idle' || phase === 'cancelled') {
						engine.off('appliance-drag:state', onDragState);
						handleSettled();
					}
				};
				engine.on('appliance-drag:state', onDragState);
				return;
			}
			if (rafId !== null) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				rafId = requestAnimationFrame(() => {
					rafId = null;
					// A non-owning instance (e.g. a rendering preview) must never call reset_view on the
					// shared controller — executeTool resolves through the controller's single engine
					// slot, so this engine's own resize would re-fit whichever OTHER engine owns that slot.
					if (props.ownsController === false) return;
					const ctrl = controllerRef.current;
					if (!ctrl?.executeTool) {
						console.warn('Render3DKitchen: resize-settled fired but controller.executeTool is unavailable');
						return;
					}
					console.debug('Render3DKitchen: calling reset_view after resize settled');
					ctrl.executeTool('reset_view', {}).catch((error: unknown) => {
						console.warn('Render3DKitchen: reset_view after resize failed:', error);
					});
				});
			});
		};

		engine.on('resize:settled', handleSettled);
		return () => {
			engine.off('resize:settled', handleSettled);
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [isInitialized, isBuildComplete]);

	// Handle zoom changes - must wait for build to complete so auto-zoom has
	// established the correct optimalDistance on the camera manager
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized || !isBuildComplete) return;

		if (props.controller?.viewport?.state?.zoom >= 10) {
			engine.setZoom(Math.round(props.controller.viewport.state.zoom));
		}
	}, [props.controller?.viewport?.state?.zoom, isInitialized, isBuildComplete]);

	// Handle sky/floor texture updates - applied immediately once initialized.
	// These don't need to wait for a build since the foundation loads them early.
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized) return;

		(async () => {
			// Handle sky/custom texture
			if (props.customTexture) {
				engine.setCustomSkyTexture(props.customTexture);
			} else if (props.skyKey && props.skyKey !== 'background') {
				await engine.changeSkyTexture(props.skyKey);
			}

			// Handle floor texture
			if (props.floorKey) {
				await engine.changeFloorTexture(props.floorKey);
			}
		})();
	}, [
		props.skyKey,
		props.floorKey,
		props.customTexture,
		isInitialized
	]);

	// Handle material texture updates - must wait for build to complete
	// since surround/countertop textures are loaded during the build.
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized || !isBuildComplete || !props.kitchen) return;

		(async () => {
			// Handle surround texture
			const surroundKey = props.kitchen?.material?.surround?.key;
			if (surroundKey) {
				const needsRebuild = await engine.changeSurroundTexture(surroundKey);
				if (needsRebuild) {
					engine.rebuild().catch(console.error);
					return;
				}
			}

			// Handle countertop texture
			const countertopKey = props.kitchen?.material?.countertop?.key;
			if (countertopKey) {
				await engine.changeCountertopTexture(countertopKey);
			}
		})();
	}, [
		props.kitchen?.material?.surround?.key,
		props.kitchen?.material?.countertop?.key,
		isInitialized,
		isBuildComplete
	]);

	// Handle viewportAnchor changes - applied immediately on init, doesn't need blueprint
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized) return;

		// Get viewport anchor from props or controller
		const anchor = props.viewportAnchor ?? props.controller?.viewportAnchor;
		if (!anchor) return;

		// Apply to camera manager
		if (engine.cameraManager) {
			engine.cameraManager.viewportAnchor = anchor;
		}
	}, [
		props.viewportAnchor,
		props.controller?.viewportAnchor,
		isInitialized
	]);

	// Handle export request
	useEffect(() => {
		if (isInitialRender.current) {
			isInitialRender.current = false;
			return;
		}

		const engine = engineRef.current;
		if (!engine || !isInitialized) return;

		if (props.controller?.requestExport) {
			engine.exportModel({ filename: 'kitchen.glb' });
		}
	}, [props.controller?.requestExport, isInitialized]);

	// Check AR capabilities on mount
	useEffect(() => {
		const checkARAvailability = async (): Promise<void> => {
			try {
				const iosQuickLookSupported = ARHelper.instance.isArQuickLookCandidate();
				const isAndroid = ARHelper.instance.isAndroid();
				let webXRSupported = false;

				try {
					const webXRCheck = Promise.race([
						ARHelper.instance.supportsPresentation(),
						new Promise<boolean>((resolve) => {
							setTimeout(() => resolve(false), 2000);
						})
					]);
					webXRSupported = await webXRCheck;
				} catch {
					// WebXR check failed
				}

				// Android is offered the WebXR launch sheet regardless of the probe
				// result: Chrome may answer false until Google Play Services for AR
				// is installed, and the sheet explains what is missing otherwise.
				setIsARAvailable(webXRSupported || iosQuickLookSupported || isAndroid);
			} catch {
				if (ARHelper.instance.isIOS() || ARHelper.instance.isAndroid()) {
					setIsARAvailable(true);
				} else {
					setIsARAvailable(false);
				}
			}
		};

		checkARAvailability();
	}, []);

	// Register AR session callbacks so WebXR status events (forwarded from the
	// model-viewer launch sheet) keep the button state in sync. iOS Quick Look
	// runs in system UI and never fires these. While the button is showing on a
	// WebXR device, warm the lazy model-viewer chunk so the sheet opens faster.
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized) return;

		engine.setARCallbacks({
			onStatusChange: (status) => {
				setArStatus(status);
				// Status transitions always close the loading phase — SESSION_STARTED
				// means AR is live, NOT_PRESENTING/FAILED means we're back to the UI.
				setIsARLoading(false);
			}
		});

		// Pre-warm only once AR is offered; a consumer can hold AR back (arEnabled
		// false) and turn it on later, which pre-warms then.
		if (isARAvailable && arEnabled) {
			engine.prewarmAR();
		}
	}, [isInitialized, isARAvailable, arEnabled]);

	// Handle AR button click
	const handleARClick = useCallback(async () => {
		const engine = engineRef.current;
		if (!engine) return;

		setArError(null);
		setIsARLoading(true);

		// Check basic requirements
		const protocol = window.location.protocol.toLowerCase();
		const hostname = window.location.hostname.toLowerCase();
		const { isSecureContext } = window;
		const isHTTPS = protocol === 'https:';
		const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
		const isFileProtocol = protocol === 'file:';
		const canUseAR = isSecureContext && !isFileProtocol && (isHTTPS || isLocalhost);

		if (!canUseAR) {
			let errorMsg = 'AR requires HTTPS or localhost. ';
			if (isFileProtocol) {
				errorMsg += 'File:// protocol is not supported.';
			} else if (!isSecureContext) {
				errorMsg += 'Current context is not secure.';
			} else {
				errorMsg += 'Please access this page over HTTPS.';
			}
			setArError(errorMsg);
			setIsARLoading(false);
			return;
		}

		try {
			await engine.activateAR();
			setIsARLoading(false);
		} catch (error: any) {
			setIsARLoading(false);
			const errorMessage = error?.message || 'Unknown error';
			let userMessage = 'Unable to start AR. ';

			if (errorMessage.includes('not ready') || errorMessage.includes('not active')) {
				userMessage += 'The kitchen model is still loading.';
			} else if (errorMessage.includes('empty')) {
				userMessage += 'The kitchen model is not ready.';
			} else if (errorMessage.includes('timeout')) {
				userMessage += 'AR export timed out. Try simplifying the design.';
			} else if (errorMessage.includes('not supported')) {
				userMessage += 'AR is not supported on this device.';
			} else {
				userMessage += errorMessage;
			}

			setArError(userMessage);
		}
	}, []);

	// Handle AR request from the controller (same path as the button so the
	// loading and error UI behave identically)
	useEffect(() => {
		if (isInitialRender2.current) {
			isInitialRender2.current = false;
			return;
		}

		const engine = engineRef.current;
		if (!engine || !isInitialized) return;

		if (props.controller?.requestAR) {
			handleARClick();
		}
	}, [props.controller?.requestAR, isInitialized, handleARClick]);

	const isARSessionActive = arStatus === ARStatus.SESSION_STARTED || arStatus === ARStatus.OBJECT_PLACED;

	return (
		<CanvasShell ref={ref} minHeight={props.canvasMinHeight}>
			<ArErrorBanner message={arError} onClose={() => setArError(null)} />
			<ArLoadingBanner isLoading={isARLoading} />
			<ArActionButton
				isVisible={arEnabled && isARAvailable && !isARLoading && !isARSessionActive}
				disabled={isARLoading}
				onClick={handleARClick}
			/>
			<RenderingOverlay isVisible={isRendering && !props.controller?.progressiveRender} />
			{initError && (
				<div style={{
					position: 'absolute',
					inset: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '16px',
					textAlign: 'center',
					color: '#4A5568',
					background: '#F7FAFC',
					font: '14px sans-serif'
				}}>
					{initError}
				</div>
			)}
		</CanvasShell>
	);
};
